import { ChangeEvent, FC, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { Import } from '@assets/icons';
import {
  Button,
  Input,
  LockedButton,
  PermissionAccess,
  Spinner,
} from '@components/index';
import { UserPermissions } from '@enums/user-permissions';
import { AssetType } from '@pages/home-page/enums';
import { useImportAssetIds } from '@pages/home-page/hooks/use-import-asset-ids';
import { workspaceAtom } from '@store/atoms/workspace';

import CustomAttributeSelectionModal from './custom-attribute-selection-modal/CustomAttributeSelectionModal';

const ID_LIMIT = 25;
// eslint-disable-next-line quotes
const ASSET_BANK_LABEL = "Upload from 'The Asset Bank'";

interface AssetIdInputProp {
  showImportButton?: boolean;
  classOverride?: string;
  showLabel?: boolean;
  assetType?: AssetType;
  onCloseClick?: () => void;
}

const AssetIdInput: FC<AssetIdInputProp> = ({
  showImportButton = false,
  classOverride = '',
  showLabel = true,
  assetType,
  onCloseClick = () => {},
}) => {
  const currentWorkspace = useRecoilValue(workspaceAtom);
  const [assetIds, setAssetIds] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showAttributeSelectionModal, setShowAttributeSelectionModal] =
    useState(false);

  const hasAssets = assetIds?.trim().length > 0;

  const fileCount = useMemo(() => {
    return assetIds.split(',').filter(id => id.trim()).length;
  }, [assetIds]);

  const { mutateAsync: importIds, isLoading } = useImportAssetIds();

  const disableImportClick = isLoading || !hasAssets || !!errorMessage;

  const processAssetIds = () => {
    if (!assetIds.trim()) {
      setErrorMessage('Asset ID is required');

      return null;
    }

    const distinctIds = assetIds
      .replaceAll(' ', '')
      .split(',')
      .reduce((list: string[], current: string) => {
        if (!list.includes(current)) {
          list.push(current.toLowerCase().trim());
        }

        return list;
      }, []);

    if (distinctIds.length > ID_LIMIT) {
      setErrorMessage(
        `You have exceeded the upload limit of ${ID_LIMIT} TAB IDs. Please adjust your selection to proceed.`,
      );

      return null;
    }

    setErrorMessage('');

    return distinctIds;
  };

  const importTabAssets = async ({
    ids,
    attributeTypeIds,
  }: {
    ids: string[];
    attributeTypeIds?: number[];
  }) => {
    try {
      await importIds({
        tabIds: ids,
        clientUuid: currentWorkspace.uuid,
        ...(assetType && { assetType }),
        ...(attributeTypeIds && { attributeTypeIds }),
      });
    } finally {
      onCloseClick();
      setAssetIds('');
    }
  };

  const handleImport = async () => {
    const distinctIds = processAssetIds();
    if (!distinctIds) return;

    // Show attribute selection modal for RAW_FILE or unspecified assetType
    if (assetType === AssetType.RAW_FILE || !assetType) {
      setShowAttributeSelectionModal(true);
    } else {
      // Direct import for other asset types
      await importTabAssets({ ids: distinctIds, attributeTypeIds: [] });
    }
  };

  const handleAttributeExtract = async (selectedAttributeIds: number[]) => {
    setShowAttributeSelectionModal(false);
    const distinctIds = processAssetIds();
    if (distinctIds) {
      await importTabAssets({
        ids: distinctIds,
        attributeTypeIds: selectedAttributeIds,
      });
    }
  };

  const handleOnchange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');
    setAssetIds(e.target.value);
  };

  return (
    <>
      <div className="w-full">
        <div className="flex items-end justify-center w-full gap-4">
          <PermissionAccess
            permission={UserPermissions.MANAGE_BRAND_LIBRARY}
            AllowedContent={
              <div className="flex align-top w-full gap-2">
                <Input
                  containerClass={twMerge('w-full', classOverride)}
                  value={assetIds}
                  placeholder="Enter Asset ID(s). Eg: 12345, 45622, etc..."
                  onChange={handleOnchange}
                  error={errorMessage}
                  label={showLabel ? ASSET_BANK_LABEL : ''}
                  onKeyDown={e => {
                    if (e.key !== 'Enter' || disableImportClick) return;
                    e.preventDefault();
                    handleImport();
                  }}
                  disabled={isLoading}
                  customInputClass="focus:border-primary focus:bg-lightest"
                />
                {!showImportButton && (
                  <div
                    className={twMerge(
                      'flex items-center justify-center rounded size-10',
                      disableImportClick
                        ? 'bg-gray-2'
                        : 'bg-primary cursor-pointer',
                    )}
                    onClick={disableImportClick ? undefined : handleImport}
                  >
                    {isLoading ? (
                      <Spinner color="bg-gray-1" />
                    ) : (
                      <Import
                        className={`${disableImportClick ? 'text-dark-3' : 'text-white'}`}
                      />
                    )}
                  </div>
                )}
              </div>
            }
            DeniedContent={
              <>
                <Input
                  containerClass={twMerge('w-full', classOverride)}
                  placeholder="Eg: 12345, 23456, etc..."
                  label={showLabel ? ASSET_BANK_LABEL : ''}
                  disabled
                />
                <LockedButton buttonClassname="w-fit" />
              </>
            }
          />
        </div>
        {showImportButton && (
          <div className="mt-4">
            <Button
              label="Import"
              variant="contained"
              isLoading={isLoading}
              isDisabled={disableImportClick}
              onClick={handleImport}
              classOverride="!text-sm"
            />
          </div>
        )}
      </div>

      <CustomAttributeSelectionModal
        isOpen={showAttributeSelectionModal}
        onClose={() => setShowAttributeSelectionModal(false)}
        onExtract={handleAttributeExtract}
        fileCount={fileCount}
      />
    </>
  );
};
export default AssetIdInput;
